import type { GeneratedGHGInventoryReport, ReportSectorObservation } from '../types';
import { hasUnusualResidentDenominator } from '../sectorPresentation';
import type { WastewaterIntroState } from './types';

export function sectorByCode(sectors: ReportSectorObservation[], code: string): ReportSectorObservation | undefined {
  return sectors.find((s) => s.sectorCode === code);
}

export function electricityLooksLikeFill(sectors: ReportSectorObservation[]): boolean {
  const check = (code: string) => {
    const s = sectorByCode(sectors, code);
    if (!s) return false;
    const mt = s.emissionsMt.value;
    const kwh = s.activity?.value;
    return mt === 0 && (kwh == null || !Number.isFinite(kwh));
  };
  return check('elec_res') || check('elec_com');
}

export function classifyWastewaterState(report: GeneratedGHGInventoryReport): WastewaterIntroState {
  const ww = sectorByCode(report.sectorDetail, 'water_wastewater');
  const combined = sectorByCode(report.sectorDetail, 'waste');
  const wwMt = ww?.emissionsMt.value ?? null;
  const combinedMt = combined?.emissionsMt.value ?? null;
  const status = ww?.sourceStatusText ?? ww?.emissionsMt.status;
  const year = report.identity.inventoryYear;

  if (wwMt == null || !Number.isFinite(wwMt)) {
    if (status === 'unresolved_breakout' || combinedMt != null) return 'unavailable';
    return 'unavailable';
  }

  const scaled =
    status === 'scaled_service_population' ||
    status === 'scaled_population_placeholder' ||
    (ww?.notes ?? '').toLowerCase().includes('scaled from');
  const yearMatched = status === 'year_matched_stored_inventory';
  const sourceYear = extractWastewaterSourceYear(ww) ?? ww?.sourceYear ?? null;

  if (combinedMt != null && Number.isFinite(combinedMt)) {
    if (scaled && sourceYear != null && sourceYear !== year) return 'combined_scaled_other_year_in_total';
    if (yearMatched || sourceYear === year) return 'combined_same_year_in_total';
    if (scaled) return 'combined_scaled_other_year_in_total';
    return 'combined_same_year_in_total';
  }

  if (scaled && sourceYear != null && sourceYear !== year) return 'available_different_year_not_in_total';
  if (yearMatched || sourceYear === year) return 'available_same_year_not_in_total';
  return 'unresolved_or_conflicting';
}

export function extractWastewaterSourceYear(sector: ReportSectorObservation | undefined): number | null {
  if (!sector) return null;
  const notes = `${sector.notes ?? ''} ${sector.emissionsMt.provenance?.calculationRule ?? ''}`;
  const fromNotes = notes.match(/scaled from the (\d{4})/i) || notes.match(/inventory for (\d{4})/i);
  if (fromNotes) {
    const y = Number(fromNotes[1]);
    return Number.isFinite(y) ? y : null;
  }
  if (typeof sector.sourceYear === 'number' && Number.isFinite(sector.sourceYear)) {
    return sector.sourceYear;
  }
  return null;
}

export function extractStoredWastewaterMt(sector: ReportSectorObservation | undefined): number | null {
  if (!sector?.notes) return null;
  const m = sector.notes.match(/inventory \(([\d.]+) MT/i);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

export function unusualDenominator(report: GeneratedGHGInventoryReport): boolean {
  const pop = report.communityProfile.population?.value;
  return hasUnusualResidentDenominator(typeof pop === 'number' ? pop : null);
}
